import React, { useState, useEffect, useCallback } from "react";
import { Building2, Briefcase, LogOut, FileText, Download, Users, Eye, ArrowLeft, EyeOff, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import "./HrDashboard.css";
import { supabase } from "../lib/supabase";

export default function HrDashboard() {
  const navigate = useNavigate();

  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [view, setView] = useState("dashboard");
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({
    title: "",
    location: "",
    type: "Full-time",
    description: ""
  });

  // Fetch jobs from database
  const fetchJobs = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('jobs')
        .select('*')
        .order('posted_date', { ascending: false });

      if (error) throw error;
      setJobs(data || []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      alert('Error loading jobs: ' + error.message);
    }
  }, []);

  // Fetch applications from database
  const fetchApplications = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('applications')
        .select(`
          *,
          jobs (
            id,
            title,
            location,
            type
          )
        `)
        .order('applied_date', { ascending: false });

      if (error) throw error;
      
      // Format the data
      const formattedData = data.map(app => ({
        ...app,
        jobTitle: app.jobs?.title || 'Unknown Job',
        jobLocation: app.jobs?.location || '',
        jobType: app.jobs?.type || '',
        // For backwards compatibility with existing code
        applicantData: {
          fullName: app.full_name,
          email: app.email,
          phone: app.phone,
          address: app.address,
          idNumber: app.id_number,
          experience: app.experience,
          education: app.education
        },
        documents: {
          cv: app.cv_url,
          id: app.id_document_url,
          certificates: app.certificates_url
        },
        appliedDate: new Date(app.applied_date).toLocaleDateString()
      }));

      setApplications(formattedData);
    } catch (error) {
      console.error('Error fetching applications:', error);
      alert('Error loading applications: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
    fetchApplications();
  }, [fetchJobs, fetchApplications]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [view]);

  const handlePostJob = async (e) => {
    e.preventDefault();

    try {
      const { data, error } = await supabase
        .from('jobs')
        .insert([{
          title: form.title,
          location: form.location,
          type: form.type,
          description: form.description,
          is_active: true
        }])
        .select();

      if (error) throw error;

      console.log('Job posted successfully:', data);
      
      // Refresh jobs list
      await fetchJobs();
      
      // Reset form
      setForm({ title: "", location: "", type: "Full-time", description: "" });
      alert("Job posted successfully!");
    } catch (error) {
      console.error('Error posting job:', error);
      alert('Error posting job: ' + error.message);
    }
  };

  const handleToggleJobVisibility = async (jobId, currentlyActive) => {
    try {
      const { error } = await supabase
        .from('jobs')
        .update({ is_active: !currentlyActive })
        .eq('id', jobId);

      if (error) throw error;

      // Refresh jobs list
      await fetchJobs();
      
      alert(currentlyActive ? 'Job hidden successfully' : 'Job unhidden successfully');
    } catch (error) {
      console.error('Error toggling job visibility:', error);
      alert('Error: ' + error.message);
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to permanently delete this job? This cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('jobs')
        .delete()
        .eq('id', jobId);

      if (error) throw error;

      // Refresh jobs list
      await fetchJobs();
      alert('Job deleted successfully');
    } catch (error) {
      console.error('Error deleting job:', error);
      alert('Error deleting job: ' + error.message);
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      localStorage.removeItem('hr_authenticated');
      navigate("/hr");
    } catch (error) {
      console.error('Logout error:', error);
      navigate("/hr");
    }
  };

  const getJobApplications = (jobId) => {
    return applications.filter((a) => String(a.job_id) === String(jobId));
  };

  const handleViewApplicants = (job) => {
    setSelectedJob(job);
    setView("applicants");
  };

  const handleViewApplicationDetail = (app) => {
    setSelectedApplication(app);
    setView("detail");
  };

  const handleUpdateStatus = async (appId, newStatus) => {
    try {
      const { error } = await supabase
        .from('applications')
        .update({ status: newStatus })
        .eq('id', appId);

      if (error) throw error;

      // Refresh applications
      await fetchApplications();
      
      // Update selected application if viewing details
      if (selectedApplication && selectedApplication.id === appId) {
        setSelectedApplication({ ...selectedApplication, status: newStatus });
      }

      alert(`Status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error updating status: ' + error.message);
    }
  };

  const handleDownloadDocument = (url, filename) => {
    if (!url || url === 'None' || url === 'null') {
      alert('No document available');
      return;
    }
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="hr-layout">
        <div style={{ padding: '40px', textAlign: 'center' }}>
          Loading dashboard...
        </div>
      </div>
    );
  }

  return (
    <div className="hr-layout">
      {/* SIDEBAR */}
      <aside className="sidebar">
        <div className="sidebar-top">
          <Building2 className="logo" />
          <h2>Company HR</h2>
        </div>

        <nav className="sidebar-menu">
          <button
            className={`menu-item ${view === "dashboard" ? "active" : ""}`}
            onClick={() => setView("dashboard")}
          >
            <Briefcase size={18} />
            Dashboard
          </button>

          <button
            className={`menu-item ${view === "applications" || view === "applicants" || view === "detail" ? "active" : ""}`}
            onClick={() => setView("applications")}
          >
            <Users size={18} />
            Applications
          </button>
        </nav>

        <button className="logout" onClick={handleLogout}>
          <LogOut size={18} />
          Logout
        </button>
      </aside>

      {/* MAIN */}
      <main className="main">
        {view === "dashboard" && (
          <>
            <div className="topbar">
              <h1>HR Dashboard</h1>
            </div>

            <div className="stats">
              <div className="stat-card">
                <h3>{jobs.filter(j => j.is_active).length}</h3>
                <p>Active Jobs</p>
              </div>

              <div className="stat-card">
                <h3>{applications.length}</h3>
                <p>Total Applications</p>
              </div>

              <div className="stat-card">
                <h3>{applications.filter(a => a.status === 'Pending').length}</h3>
                <p>Pending Review</p>
              </div>
            </div>

            <div className="grid">
              <div className="card">
                <h3>Post Job</h3>
                <form onSubmit={handlePostJob}>
                  <input
                    placeholder="Job title"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    required
                  />
                  <input
                    placeholder="Location"
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    required
                  />
                  <select
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                  >
                    <option>Full-time</option>
                    <option>Part-time</option>
                    <option>Contract</option>
                    <option>Internship</option>
                  </select>
                  <textarea
                    placeholder="Description"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    required
                  />
                  <button className="primary">Post Job</button>
                </form>
              </div>

              <div className="card">
                <h3>All Jobs</h3>
                {jobs.length === 0 ? (
                  <p className="no-data">No jobs posted yet</p>
                ) : (
                  jobs.map((job) => (
                    <div key={job.id} className="job-row">
                      <div>
                        <h4>
                          {job.title}
                          {!job.is_active && <span style={{color: '#dc2626', marginLeft: '8px', fontSize: '12px'}}>(Hidden)</span>}
                        </h4>
                        <p>{job.location} • {job.type}</p>
                      </div>
                      <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                        <span className="badge">{getJobApplications(job.id).length} apps</span>
                        <button
                          onClick={() => handleToggleJobVisibility(job.id, job.is_active)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px',
                            color: job.is_active ? '#6b7280' : '#16a34a'
                          }}
                          title={job.is_active ? 'Hide job' : 'Unhide job'}
                        >
                          {job.is_active ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                        <button
                          onClick={() => handleDeleteJob(job.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px',
                            color: '#dc2626'
                          }}
                          title="Delete job permanently"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </>
        )}

        {view === "applications" && (
          <>
            <div className="topbar">
              <h1>Applications</h1>
            </div>

            <div className="applications-grid">
              {jobs.length === 0 ? (
                <p className="no-data">No jobs posted yet</p>
              ) : (
                jobs.map((job) => (
                  <div key={job.id} className="application-job-card">
                    <h3>{job.title}</h3>
                    <p className="job-meta">{job.location} • {job.type}</p>
                    <p className="job-meta" style={{fontSize: '12px', color: job.is_active ? '#16a34a' : '#dc2626'}}>
                      {job.is_active ? 'Active' : 'Hidden'}
                    </p>
                    <button
                      className="app-count-badge"
                      onClick={() => handleViewApplicants(job)}
                    >
                      {getJobApplications(job.id).length} Application{getJobApplications(job.id).length !== 1 ? "s" : ""}
                    </button>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {view === "applicants" && selectedJob && (
          <>
            <div className="page-header">
              <button className="back-link" onClick={() => setView("applications")}>
                <Building2 size={20} />
                Back to Dashboard
              </button>
              <button className="logout-link" onClick={handleLogout}>
                <LogOut size={18} />
                Logout
              </button>
            </div>

            <div className="detail-container">
              <div className="detail-header-card">
                <h1>Applications for {selectedJob.title}</h1>
                <div className="applicants-count">
                  <h2>Applicants ({getJobApplications(selectedJob.id).length})</h2>
                </div>
              </div>

              {getJobApplications(selectedJob.id).length === 0 ? (
                <p className="no-data">No applications yet</p>
              ) : (
                getJobApplications(selectedJob.id).map((app) => (
                  <div key={app.id} className="modern-applicant-card">
                    <div className="applicant-info">
                      <h4>{app.applicantData?.fullName || app.full_name}</h4>
                      <p className="applicant-email">{app.applicantData?.email || app.email}</p>
                      <p className="applicant-date">Applied: {app.appliedDate}</p>
                    </div>
                    <div className="applicant-actions">
                      <span className={`modern-status-badge status-${app.status.toLowerCase().replace(/ /g, "-")}`}>
                        {app.status}
                      </span>
                      <button className="view-btn" onClick={() => handleViewApplicationDetail(app)}>
                        <Eye size={16} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {view === "detail" && selectedApplication && (
          <>
            <div className="page-header">
              <button className="back-link" onClick={() => setView("applicants")}>
                <ArrowLeft size={20} />
                Back to Applications
              </button>
              <button className="logout-link" onClick={handleLogout}>
                <LogOut size={18} />
                Logout
              </button>
            </div>

            <div className="detail-container">
              <div className="detail-header-card">
                <div className="detail-title-row">
                  <h1>{selectedJob?.title}</h1>
                  <span className={`modern-status-badge status-${selectedApplication.status.toLowerCase().replace(/ /g, "-")}`}>
                    {selectedApplication.status}
                  </span>
                </div>
                <p className="detail-subtitle">Application ID: {selectedApplication.id}</p>
                <p className="detail-subtitle">Submitted: {selectedApplication.appliedDate}</p>
              </div>

              <div className="modern-section-card">
                <h2 className="modern-section-title">Personal Information</h2>
                <div className="modern-info-grid">
                  <div className="modern-info-item">
                    <label>Full Name</label>
                    <p>{selectedApplication.applicantData?.fullName || selectedApplication.full_name}</p>
                  </div>
                  <div className="modern-info-item">
                    <label>Email</label>
                    <p>{selectedApplication.applicantData?.email || selectedApplication.email}</p>
                  </div>
                  <div className="modern-info-item">
                    <label>Phone</label>
                    <p>{selectedApplication.applicantData?.phone || selectedApplication.phone}</p>
                  </div>
                  {/* <div className="modern-info-item">
                    <label>ID Number</label>
                    <p>{selectedApplication.applicantData?.idNumber || selectedApplication.id_number}</p>
                  </div> */}
                </div>
                <div className="modern-info-full">
                  <label>Address</label>
                  <p>{selectedApplication.applicantData?.address || selectedApplication.address}</p>
                </div>
              </div>

              <div className="modern-section-card">
                <h2 className="modern-section-title">Professional Background</h2>
                <div className="modern-text-section">
                  <div className="modern-text-item">
                    <label>Work Experience</label>
                    <p>{selectedApplication.applicantData?.experience || selectedApplication.experience}</p>
                  </div>
                  <div className="modern-text-item">
                    <label>Education</label>
                    <p>{selectedApplication.applicantData?.education || selectedApplication.education}</p>
                  </div>
                </div>
              </div>

              <div className="modern-section-card">
                <h2 className="modern-section-title">Submitted Documents</h2>
                <div className="modern-documents">
                  {(selectedApplication.documents?.cv || selectedApplication.cv_url) && (
                    <div className="modern-doc-item">
                      <div className="modern-doc-info">
                        <FileText size={20} />
                        <div>
                          <p className="modern-doc-title">CV/Resume</p>
                          <p className="modern-doc-filename">Click to view</p>
                        </div>
                      </div>
                      <button 
                        className="modern-download-btn"
                        onClick={() => handleDownloadDocument(selectedApplication.documents?.cv || selectedApplication.cv_url, 'CV')}
                      >
                        <Download size={16} />
                        View/Download
                      </button>
                    </div>
                  )}
                  {/* {(selectedApplication.documents?.id || selectedApplication.id_document_url) && (
                    <div className="modern-doc-item">
                      <div className="modern-doc-info">
                        <FileText size={20} />
                        <div>
                          <p className="modern-doc-title">ID Document</p>
                          <p className="modern-doc-filename">Click to view</p>
                        </div>
                      </div>
                      <button 
                        className="modern-download-btn"
                        onClick={() => handleDownloadDocument(selectedApplication.documents?.id || selectedApplication.id_document_url, 'ID')}
                      >
                        <Download size={16} />
                        View/Download
                      </button>
                    </div>
                  )} */}
                  {(selectedApplication.documents?.certificates || selectedApplication.certificates_url) && (
                    <div className="modern-doc-item">
                      <div className="modern-doc-info">
                        <FileText size={20} />
                        <div>
                          <p className="modern-doc-title">Certificates</p>
                          <p className="modern-doc-filename">Click to view</p>
                        </div>
                      </div>
                      <button 
                        className="modern-download-btn"
                        onClick={() => handleDownloadDocument(selectedApplication.documents?.certificates || selectedApplication.certificates_url, 'Certificates')}
                      >
                        <Download size={16} />
                        View/Download
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="modern-section-card">
                <h2 className="modern-section-title">Update Application Status</h2>
                <div className="modern-status-actions">
                  <button
                    onClick={() => handleUpdateStatus(selectedApplication.id, "Pending")}
                    className="modern-status-btn modern-status-pending"
                  >
                    Pending
                  </button>
                  <button
                    onClick={() => handleUpdateStatus(selectedApplication.id, "Under Review")}
                    className="modern-status-btn modern-status-under-review"
                  >
                    Under Review
                  </button>
                  <button
                    onClick={() => handleUpdateStatus(selectedApplication.id, "Accepted")}
                    className="modern-status-btn modern-status-accepted"
                  >
                    Accepted
                  </button>
                  <button
                    onClick={() => handleUpdateStatus(selectedApplication.id, "Rejected")}
                    className="modern-status-btn modern-status-rejected"
                  >
                    Rejected
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}